import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { RULE_LIMITS, SEAT_NAMES, scheduledDealCount, shuffle, teamOfSeat, type HouseRules, type Seat, type TeamId } from '../game/match';
import { cleanName } from '../shared/constants';
import { HouseRulesPanel } from '../components/HouseRules';
import { Avatar, Name, Segmented, Sheet } from '../components/ui';
import { NetworkError, api } from '../lib/api';
import { TEAM_COLORS, emptyDraft, nameErrors, resetDraft, updateDraft, useDraft, type Draft } from '../lib/draft';
import { navigate } from '../lib/router';
import { rememberHostKey } from '../lib/room';
import { createMatch, rememberPlayers, rememberTeamSet } from '../lib/store';
import type { CreateRoomResponse } from '../shared/room';
import { toast } from '../lib/toast';
import { BackButton, NotFound, ScreenHeader } from './shared';

const AREA: Record<Seat, string> = {
  0: 'col-start-2 row-start-1',
  1: 'col-start-3 row-start-2',
  2: 'col-start-2 row-start-3',
  3: 'col-start-1 row-start-2',
};

function swapSeats(a: Seat, b: Seat) {
  if (a === b) return;
  updateDraft((d) => {
    const seatOrder = d.seatOrder.slice();
    [seatOrder[a], seatOrder[b]] = [seatOrder[b]!, seatOrder[a]!];
    return { ...d, seatOrder };
  });
}

/** Seats around the table: drag a player onto another seat, or tap/Enter two seats to swap. */
function SeatPicker({ draft, onAnnounce }: { draft: Draft; onAnnounce: (msg: string) => void }) {
  const [selected, setSelected] = useState<Seat | null>(null);
  const [drag, setDrag] = useState<{ seat: Seat; x: number; y: number; over: Seat | null } | null>(null);
  const start = useRef<{ seat: Seat; x: number; y: number; moved: boolean } | null>(null);
  // A drag ends with a click on the origin seat; ignore that one.
  const suppressClick = useRef(false);
  const playerAt = (seat: Seat) => draft.players[draft.seatOrder[seat]!]!;

  const choose = (seat: Seat) => {
    if (selected === null) {
      setSelected(seat);
      onAnnounce(`${playerAt(seat).name} selected. Choose a seat to swap with.`);
    } else {
      if (selected !== seat) onAnnounce(`Swapped ${playerAt(selected).name} and ${playerAt(seat).name}.`);
      swapSeats(selected, seat);
      setSelected(null);
    }
  };

  const seatUnder = (x: number, y: number): Seat | null => {
    const el = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-seat]');
    return el ? (Number(el.dataset.seat) as Seat) : null;
  };

  const onDown = (seat: Seat) => (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    start.current = { seat, x: e.clientX, y: e.clientY, moved: false };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: ReactPointerEvent) => {
    const s = start.current;
    if (!s) return;
    if (!s.moved && Math.hypot(e.clientX - s.x, e.clientY - s.y) > 8) s.moved = true;
    if (s.moved) setDrag({ seat: s.seat, x: e.clientX, y: e.clientY, over: seatUnder(e.clientX, e.clientY) });
  };
  const onUp = (e: ReactPointerEvent) => {
    const s = start.current;
    start.current = null;
    setDrag(null);
    if (!s?.moved) return;
    suppressClick.current = true;
    setTimeout(() => (suppressClick.current = false), 0);
    const target = seatUnder(e.clientX, e.clientY);
    if (target !== null && target !== s.seat) {
      onAnnounce(`Swapped ${playerAt(s.seat).name} and ${playerAt(target).name}.`);
      swapSeats(s.seat, target);
    }
    setSelected(null);
  };

  return (
    <div className="relative">
      <div className="grid grid-cols-[1fr_1.1fr_1fr] grid-rows-[auto_7rem_auto] items-center gap-2">
        <div className="col-start-2 row-start-2 grid h-full place-items-center rounded-[2rem] bg-[var(--color-table-felt)] text-center text-xs font-semibold text-cream/80 shadow-[inset_0_0_25px_rgb(0_0_0/0.5)] ring-4 ring-[#5b3a1e]">
          <span>
            Partners
            <br />
            sit opposite
          </span>
        </div>
        {([0, 1, 2, 3] as Seat[]).map((seat) => {
          const p = playerAt(seat);
          const team = draft.teams[teamOfSeat(seat)];
          const isSel = selected === seat;
          const isOver = drag?.over === seat && drag.seat !== seat;
          return (
            <button
              key={seat}
              type="button"
              data-seat={seat}
              onPointerDown={onDown(seat)}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={() => {
                start.current = null;
                setDrag(null);
              }}
              onKeyDown={(e) => e.key === 'Escape' && setSelected(null)}
              // Taps, Enter/Space and screen-reader activation all arrive as clicks.
              onClick={() => {
                if (suppressClick.current) suppressClick.current = false;
                else choose(seat);
              }}
              aria-pressed={isSel}
              aria-label={`${SEAT_NAMES[seat]} seat, ${team.name}: ${p.name}. ${isSel ? 'Selected.' : 'Press to select, then choose another seat to swap.'}`}
              className={`flex touch-none flex-col items-center rounded-2xl px-1 py-2 transition select-none ${AREA[seat]} ${
                isSel ? 'bg-cream/20 ring-2 ring-gold' : isOver ? 'bg-cream/25 ring-2 ring-cream' : 'hover:bg-white/5'
              } ${drag?.seat === seat ? 'opacity-40' : ''}`}
            >
              <Avatar name={p.name} avatarKey={p.avatarKey} size={56} ring={team.color} />
              <Name className="mt-1 max-w-full truncate text-sm font-semibold">{p.name}</Name>
              <span className="text-[11px] font-semibold text-cream/75">
                {SEAT_NAMES[seat]} · <span style={{ color: 'inherit' }}>{team.name}</span>
              </span>
            </button>
          );
        })}
      </div>
      {drag && (
        <div className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-1/2" style={{ left: drag.x, top: drag.y }} aria-hidden>
          <Avatar name={playerAt(drag.seat).name} avatarKey={playerAt(drag.seat).avatarKey} size={64} ring="var(--color-gold)" />
        </div>
      )}
    </div>
  );
}

function TeamEditor({ id, draft }: { id: TeamId; draft: Draft }) {
  const team = draft.teams[id];
  const other = draft.teams[id === 'A' ? 'B' : 'A'];
  const setTeam = (patch: Partial<typeof team>) =>
    updateDraft((d) => ({ ...d, teams: { ...d.teams, [id]: { ...d.teams[id], ...patch } } }));
  const seats = id === 'A' ? 'North + South' : 'East + West';
  return (
    <div className="card p-4">
      <label className="label" htmlFor={`team-${id}`}>
        Team {id} <span className="font-normal">· {seats}</span>
      </label>
      <input
        id={`team-${id}`}
        dir="auto"
        className="field name font-semibold"
        value={team.name}
        maxLength={24}
        onChange={(e) => setTeam({ name: e.target.value })}
        onBlur={() => setTeam({ name: cleanName(team.name) || `Team ${id}` })}
      />
      <fieldset className="mt-3">
        <legend className="sr-only">Team {id} colour</legend>
        <div className="flex flex-wrap gap-2">
          {TEAM_COLORS.map((c) => (
            <label key={c} className="relative">
              <input
                type="radio"
                name={`color-${id}`}
                value={c}
                checked={team.color === c}
                disabled={other.color === c}
                onChange={() => setTeam({ color: c })}
                className="peer sr-only"
                aria-label={`Colour ${c}${other.color === c ? ' (used by the other team)' : ''}`}
              />
              <span
                className="block h-9 w-9 cursor-pointer rounded-full ring-offset-2 ring-offset-cream transition peer-checked:ring-3 peer-checked:ring-ink peer-focus-visible:outline-3 peer-focus-visible:outline-gold peer-disabled:cursor-not-allowed peer-disabled:opacity-25"
                style={{ background: c }}
              />
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  );
}

/** Bandh Rang plays to ±100, so instead of a deal count: the scoring at a glance, and the turn direction. */
function BandhRulesCard({ rules, onChange }: { rules: HouseRules; onChange: (r: HouseRules) => void }) {
  return (
    <section className="card mb-4 p-4" aria-labelledby="bandh-h">
      <h2 id="bandh-h" className="font-display text-lg leading-none font-extrabold">
        Bandh Rang
      </h2>
      <ul className="mt-3 grid grid-cols-3 gap-2 text-center">
        <li className="rounded-xl bg-success-600/10 px-2 py-2.5 ring-1 ring-success-600/25">
          <span className="block font-display text-xl font-extrabold text-success-700 tabular-nums">−26</span>
          <span className="text-[10px] font-bold text-ink-soft uppercase">Run</span>
        </li>
        <li className="rounded-xl bg-suit-red/8 px-2 py-2.5 ring-1 ring-suit-red/25">
          <span className="block font-display text-xl font-extrabold text-suit-red tabular-nums">+13</span>
          <span className="text-[10px] font-bold text-ink-soft uppercase">Stopped</span>
        </li>
        <li className="rounded-xl bg-ink/5 px-2 py-2.5 ring-1 ring-ink/15">
          <span className="block font-display text-xl font-extrabold tabular-nums">100</span>
          <span className="text-[10px] font-bold text-ink-soft uppercase">Loses</span>
        </li>
      </ul>
      <p className="mt-2.5 text-xs text-ink-soft">
        The dealer gives the first 5 cards to the next player, who picks a hidden Rang — their team runs, the dealing team stops them. A stopped run is +13 to the running
        team, a run is −26. The team on the plus side deals next; the score never resets, and the first team to 100 loses.
      </p>
      <div className="mt-3">
        <Segmented
          label="Turn direction"
          value={rules.turnDirection}
          onChange={(turnDirection) => onChange({ ...rules, turnDirection })}
          options={[
            { value: 'anticlockwise', label: '↺ Anticlockwise' },
            { value: 'clockwise', label: '↻ Clockwise' },
          ]}
        />
      </div>
    </section>
  );
}

/** How many deals each player gets — 3 by default, each dealt in a row before the deck passes on. */
function DealsPicker({ rules, onChange }: { rules: HouseRules; onChange: (r: HouseRules) => void }) {
  const n = rules.dealsPerPlayer;
  const inARow = rules.dealsPerTurn;
  const choose = (v: number) =>
    // Keep "pass every hand" tables as they are; otherwise the dealer deals all of theirs in a row.
    onChange({ ...rules, dealsPerPlayer: v, dealsPerTurn: rules.dealsPerTurn === 1 ? 1 : v });
  return (
    <section className="card mb-4 p-4" aria-labelledby="deals-h">
      <div className="flex items-baseline justify-between gap-2">
        <h2 id="deals-h" className="font-display text-lg leading-none font-extrabold">
          Deals each
        </h2>
        <span className="text-xs font-semibold text-ink-soft">{scheduledDealCount(rules)} deals in the match</span>
      </div>
      <div className="mt-3 grid grid-cols-6 gap-1.5" role="radiogroup" aria-label="Deals per player">
        {Array.from({ length: RULE_LIMITS.dealsPerPlayer.max - RULE_LIMITS.dealsPerPlayer.min + 1 }, (_, i) => i + RULE_LIMITS.dealsPerPlayer.min).map((v) => (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={n === v}
            onClick={() => choose(v)}
            className={`relative h-12 rounded-xl font-display text-xl font-extrabold tabular-nums transition ${
              n === v ? 'bg-ink text-cream shadow-[0_8px_18px_-8px_rgb(0_0_0/0.7)] ring-2 ring-gold' : 'bg-white text-ink ring-1 ring-ink/15 hover:ring-ink/40'
            }`}
          >
            {v}
            {v === 3 && (
              <span className={`absolute -top-2 left-1/2 -translate-x-1/2 rounded-full px-1.5 text-[8px] font-extrabold tracking-wider uppercase ${n === v ? 'bg-gold text-ink' : 'bg-ink/10 text-ink-soft'}`}>
                Usual
              </span>
            )}
          </button>
        ))}
      </div>
      <p className="mt-2.5 text-xs text-ink-soft">
        {inARow === 1
          ? `Each player deals ${n} ${n === 1 ? 'time' : 'times'}, passing the deck after every hand.`
          : `Each player deals ${inARow === n ? `${n} ${n === 1 ? 'hand' : 'hands in a row'}` : `${inARow} in a row, ${n} in all`} before the deck passes on.`}{' '}
        You can keep playing after the last deal.
      </p>
    </section>
  );
}

export function Teams() {
  const draft = useDraft();
  const [mode, setMode] = useState<'random' | 'manual'>('manual');
  const [rulesOpen, setRulesOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [announce, setAnnounce] = useState('');

  if (nameErrors(draft.players).some(Boolean)) {
    return <NotFound message="Add four players first." />;
  }

  const randomize = () => {
    updateDraft((d) => ({ ...d, seatOrder: shuffle([0, 1, 2, 3]) }));
    setAnnounce('Teams shuffled.');
  };

  const create = async () => {
    setCreating(true);
    try {
      const seats = draft.seatOrder.map((i) => {
        const p = draft.players[i]!;
        return { playerId: p.playerId, name: cleanName(p.name), avatarKey: p.avatarKey };
      });
      if (draft.mode === 'online') {
        const { code, hostKey } = await api<CreateRoomResponse>('/rooms', { method: 'POST', body: { seats, teams: draft.teams, rules: draft.rules, variant: draft.variant ?? 'open' } });
        rememberHostKey(code, hostKey);
        rememberPlayers(seats, true);
        rememberTeamSet(seats, draft.teams);
        resetDraft(emptyDraft());
        navigate(`/r/${code}`, { replace: true });
        return;
      }
      const code = await createMatch({ seats, teams: draft.teams, rules: draft.rules });
      resetDraft(emptyDraft());
      navigate(`/m/${code}/wheel`, { replace: true });
    } catch (err) {
      toast(
        err instanceof NetworkError ? 'Creating a match needs a connection. Try again when online.' : (err as Error).message,
        'error',
        6000,
      );
      setCreating(false);
    }
  };

  const r = draft.rules;
  const bandh = draft.mode === 'online' && draft.variant === 'bandh';
  return (
    <main className="mx-auto max-w-lg px-4 pb-32">
      <ScreenHeader
        step={draft.mode === 'online' ? `2 of 2 · ${bandh ? 'Bandh Rang' : 'Open Rang'}` : '2 of 3'}
        title="Seats & teams"
        left={<BackButton href="/new" />}
      />

      <div className="card mb-4 p-4">
        <Segmented
          label="How should teams be formed?"
          value={mode}
          onChange={(m) => {
            setMode(m);
            if (m === 'random') randomize();
          }}
          options={[
            { value: 'manual', label: 'Choose seats' },
            { value: 'random', label: '🎲 Random teams' },
          ]}
        />
        <p className="mt-2 text-sm text-ink-soft">
          {mode === 'manual' ? 'Drag a player onto another seat, or tap two players to swap them.' : 'Seats were shuffled fairly. Not happy? Shuffle again.'}
        </p>
        {mode === 'random' && (
          <button type="button" className="btn btn-ink mt-3 w-full" onClick={randomize}>
            🎲 Shuffle again
          </button>
        )}
      </div>

      <section aria-label="Table" className="panel mb-4 p-3">
        <SeatPicker draft={draft} onAnnounce={setAnnounce} />
        <p className="sr-only" aria-live="polite">
          {announce}
        </p>
      </section>

      {bandh ? (
        <BandhRulesCard rules={draft.rules} onChange={(rules) => updateDraft((d) => ({ ...d, rules }))} />
      ) : (
        <DealsPicker rules={draft.rules} onChange={(rules) => updateDraft((d) => ({ ...d, rules }))} />
      )}

      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <TeamEditor id="A" draft={draft} />
        <TeamEditor id="B" draft={draft} />
      </div>

      {!bandh && (
      <button type="button" className="card flex w-full items-center gap-3 p-4 text-left" onClick={() => setRulesOpen(true)}>
        <span className="text-2xl" aria-hidden>
          📜
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold">House rules</span>
          <span className="block text-sm text-ink-soft">
            Guaa · {r.turnDirection === 'anticlockwise' ? 'anticlockwise' : 'clockwise'} ·{' '}
            {r.dealsPerTurn > 1 ? `${r.dealsPerTurn} hands per dealer` : 'pass every hand'} · {scheduledDealCount(r)} deals
            {r.court.enabled ? ` · Court +${r.court.bonus}` : ''}
          </span>
        </span>
        <span aria-hidden>›</span>
      </button>
      )}

      <div className="fixed inset-x-0 bottom-0 bg-gradient-to-t from-felt-950 via-felt-950/90 to-transparent px-4 pt-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto max-w-lg">
          <button type="button" className="btn btn-primary h-14 w-full text-lg" onClick={create} disabled={creating}>
            {draft.mode === 'online'
              ? creating
                ? 'Opening table…'
                : 'Open online table →'
              : creating
                ? 'Creating match…'
                : 'Create match & spin for dealer →'}
          </button>
        </div>
      </div>

      <Sheet
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        title="House rules"
        footer={
          <button type="button" className="btn btn-ink w-full" onClick={() => setRulesOpen(false)}>
            Done
          </button>
        }
      >
        <HouseRulesPanel rules={draft.rules} onChange={(rules) => updateDraft((d) => ({ ...d, rules }))} />
      </Sheet>
    </main>
  );
}
